// Admin — Atlas IA · Producers Claims.
//
// Handles the Producer-category suggestions specifically. For each
// claim, the admin can either LINK it to an existing producer or
// GENERATE a new producer fiche via DeepSeek (which lands as a draft
// bundle in monthly_theme_bundles).
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Loader2, Zap, ExternalLink } from "lucide-react";
import { api } from "@/pages/admin/inboundApi";

const AdminAtlasClaimsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api("/api/admin/inbound/suggestions?category=producer&status=new&limit=200");
      setItems(r.items || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async (key) => {
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const r = await api("/api/admin/inbound/claims/generate", { method: "POST", body: JSON.stringify({ key }) });
      window.alert(`Génération lancée · bundle ${r.bundle_id}. Ouvre-le depuis Thème du mois pour valider.`);
      load();
    } catch (e) { setError(e.message); }
    finally { setBusy((b) => ({ ...b, [key]: false })); }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="admin-atlas-claims">
      <div className="flex items-center gap-3 mb-6">
        <Shield size={26} className="text-[color:var(--gv-pink)]" />
        <h1 className="text-3xl font-serif">Atlas IA · Producers Claims</h1>
      </div>

      <p className="text-[13.5px] text-[color:var(--gv-ink)]/60 mb-6 max-w-2xl">
        Les claims sont des suggestions de producteurs remontés par les utilisateurs. Pour chaque claim :
        soit tu l'associes à une fiche existante, soit tu lances une génération DeepSeek (fiche en
        brouillon dans Thème du mois pour validation).
      </p>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-[5px] text-[13px] mb-4">{error}</div>}

      {loading ? (
        <div className="py-8 text-center"><Loader2 size={20} className="animate-spin inline text-[color:var(--gv-pink)]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-[color:var(--gv-line)] rounded-[5px] py-12 text-center text-[color:var(--gv-ink)]/55" data-testid="empty">
          Aucun claim producteur en attente. Bravo — la file est vide.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => (
            <li key={it.key} className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-4 flex items-start justify-between gap-4" data-testid={`claim-${it.key}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-[15px] mb-1">{it.first_query || it.last_query}</div>
                <div className="text-[11.5px] text-[color:var(--gv-ink)]/55 flex flex-wrap items-center gap-2">
                  <span>×{it.count} demandes</span>
                  <span>· langs {(it.langs || []).join("/")}</span>
                  <span>· sources {(it.sources || []).join("/")}</span>
                  <span>· vu {it.last_seen_at?.slice(0, 16).replace("T", " ")}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {it.linked_bundle_id ? (
                  <Link to={`/admin/monthly-theme?bundle=${it.linked_bundle_id}`} className="inline-flex items-center gap-1.5 bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)] px-3 py-2 rounded-[5px] text-[12.5px]" data-testid={`open-bundle-${it.key}`}>
                    Voir bundle <ExternalLink size={11} />
                  </Link>
                ) : (
                  <button
                    onClick={() => generate(it.key)}
                    disabled={busy[it.key]}
                    className="inline-flex items-center gap-1.5 bg-[color:var(--gv-pink)] hover:opacity-90 text-white px-3 py-2 rounded-[5px] text-[12.5px] disabled:opacity-40"
                    data-testid={`generate-${it.key}`}
                  >
                    {busy[it.key] ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                    Générer fiche
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminAtlasClaimsPage;
